import { queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { useIntl } from '@leagueplatform/locales';
import { FullScreenContainer } from 'components/full-screen-container/full-screen-container.component';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import { CONTAINER_BACKGROUND_COLOR } from 'types/patient-portal-system.types';
import { Stepper } from 'components/stepper/stepper.component';
import { ErrorState } from 'components/error-state/error-state.component';
import { ERROR_TYPES } from 'hooks/use-error-content/use-error-content.hook';
import { TermsStep } from './form-steps/terms/terms-step.component';
import { MarketingStep } from './form-steps/marketing/marketing-step.component';
import { MobileNumberStep } from './form-steps/mobile-number/mobile-number-step.component';
import { CodeVerificationStep } from './form-steps/code-verification/code-verification-step.component';
import { PersonalInformationStep } from './form-steps/personal-information/personal-information-step.component';
import { PersonalInfoReviewStep } from './form-steps/personal-information/personal-information-review-step.component';
import { CompleteStep } from './form-steps/complete/complete-step.component';
import { STEP_IDS } from './onboarding-account-setup.constants';
import {
  OnboardingErrorProvider,
  useOnboardingError,
} from './contexts/onboarding-error.context';
import { OnboardingFormProvider } from './contexts/onboarding-form.context';
import {
  OnboardingProfileProvider,
  useOnboardingProfile,
} from './contexts/onboarding-profile.context';
import { useMobileVerificationDisabled } from './hooks/use-disable-mobile-verification/use-disable-mobile-verification.hook';

interface OnboardingAccountSetupProps {
  onOnboardingComplete: (error?: Error) => void;
}

const OnboardingAccountSetupInner = ({
  onOnboardingComplete,
}: OnboardingAccountSetupProps) => {
  const { error, reportError } = useOnboardingError();

  const isMobile = useMediaQuery(queryHelpers.down('tablet'));

  const { systemId } = usePatientPortalContext();
  const { profileData, stepFlags } = useOnboardingProfile();

  const { formatMessage } = useIntl();
  useDocumentTitle(formatMessage({ id: 'STR_WELCOME' }));

  const { isMobileVerificationDisabled, DUMMY_MOBILE_NUMBER } =
    useMobileVerificationDisabled();
  const primaryMobileNumber = profileData?.phoneNumber;
  const mobileNumber = isMobileVerificationDisabled
    ? DUMMY_MOBILE_NUMBER
    : primaryMobileNumber;

  if (error) {
    return (
      <ErrorState
        isFullPage
        errorType={ERROR_TYPES.API_ERROR}
        error={error}
        buttonAction={() => {
          reportError(null);
          window.location.reload();
        }}
      />
    );
  }

  return (
    <OnboardingFormProvider primaryMobileNumber={mobileNumber}>
      <FullScreenContainer
        horizontalAlignment="center"
        css={{
          backgroundColor: isMobile
            ? 'transparent'
            : CONTAINER_BACKGROUND_COLOR[systemId],
        }}
      >
        <Stepper>
          {stepFlags.shouldRenderTermsStep && (
            <TermsStep key={STEP_IDS.terms} />
          )}
          {stepFlags.shouldRenderMarketingStep && (
            <MarketingStep key={STEP_IDS.marketing} />
          )}
          {stepFlags.shouldRenderPhoneNumberStep &&
            !isMobileVerificationDisabled && [
              <MobileNumberStep key={STEP_IDS.mobile} />,
              <CodeVerificationStep key={STEP_IDS.verify} />,
            ]}
          {stepFlags.shouldRenderPersonalInformationStep && [
            <PersonalInformationStep key={STEP_IDS.personalInformation} />,
            <PersonalInfoReviewStep key={STEP_IDS.personalInfoReview} />,
          ]}
          <CompleteStep
            onComplete={() => onOnboardingComplete()}
            key={STEP_IDS.complete}
          />
        </Stepper>
      </FullScreenContainer>
    </OnboardingFormProvider>
  );
};

export const OnboardingAccountSetup = (props: OnboardingAccountSetupProps) => (
  <OnboardingErrorProvider>
    <OnboardingProfileProvider>
      <OnboardingAccountSetupInner {...props} />
    </OnboardingProfileProvider>
  </OnboardingErrorProvider>
);
